import styled, { css, CSSProp } from "styled-components"

import { ElementProps, elementCss } from "legacy/components/shared/base"
import { ThemeProps } from "src/legacy/theme"

export type ButtonVariant =
  | "inline"
  | "inlineNavy"
  | "primary"
  | "primaryAqua"
  | "primarySky"
  | "primarySkyDarkBackground"
  | "primaryWhite"
  | "primaryWhiteSkyBorder"
  | "primaryYellow"
  | "secondary"
  | "square"
export type ButtonSize = "default" | "secondary" | "small"

interface OwnProps {
  theme: ThemeProps
  variant?: ButtonVariant
  sizeVariant?: ButtonSize
}

export type ButtonProps = ElementProps & OwnProps

function beforeElementStyles({ theme, variant }: ButtonProps): CSSProp {
  switch (variant) {
    case "secondary":
      return css`
        position: relative;

        &:before {
          @media (max-width: 52em) {
            max-width: none;
          }

          background: ${theme.colors.navy};
          bottom: 0;
          content: "";
          display: block;
          height: 2px;
          left: 0;
          max-width: 0;
          padding: 0;
          position: absolute;
          transition: all 150ms ease;
          width: 100%;
        }

        &:hover:before {
          max-width: 100%;
        }
      `
    default:
      return css`
        /* :before element is required to adjust for Martel Sans' baseline  */
        /* If font-face is changed, this will likely need to change as well */
        &:before {
          content: "";
          height: 0;
          padding-top: ${theme.space.lineHeightOffset};
          display: block;
        }
      `
  }
}

function borderRadiusStyles({
  theme,
  variant = "primary",
}: ButtonProps): CSSProp {
  variant = variant === "inlineNavy" ? "inline" : variant

  switch (variant) {
    case "inline":
      return css`
        border-bottom-right-radius: ${theme.radii.button};
        border-top-right-radius: ${theme.radii.button};
      `
    case "secondary":
      return css`
        border-radius: 0;
      `
    case "square":
      return css`
        border-radius: ${theme.radii.buttonSquare};
      `
    default:
      return css`
        border-radius: ${theme.radii.button};
      `
  }
}

function focusStyles({ theme }: ButtonProps): CSSProp {
  return css`
    /* Default outline does not have rounded corners         */
    /* We disable it here and use the :after element instead */
    &:focus {
      outline: none;
    }

    &:focus:after {
      @media (max-width: 40em) {
        bottom: -4px;
        left: -4px;
        right: -4px;
        top: -4px;
      }

      border-radius: ${theme.radii.focus};
      border: ${theme.borders.focus};
      bottom: -5px;
      box-shadow: ${theme.shadows.focus};
      content: "";
      display: "block";
      left: -5px;
      position: absolute;
      right: -5px;
      top: -5px;
      z-index: 1;
    }
  `
}

function sizeVariantStyles({
  theme,
  sizeVariant = "default",
}: ButtonProps): CSSProp {
  switch (sizeVariant) {
    case "secondary":
      return css`
        padding: 0;
      `
    case "small":
      return css`
        padding-bottom: ${theme.space.button.gutterYSmall};
        padding-left: ${theme.space.button.gutterXSmall};
        padding-right: ${theme.space.button.gutterXSmall};
        padding-top: ${theme.space.button.gutterYSmall};
      `
    default:
      return css`
        padding-bottom: ${theme.space.button.gutterY};
        padding-left: ${theme.space.button.gutterX};
        padding-right: ${theme.space.button.gutterX};
        padding-top: ${theme.space.button.gutterY};
      `
  }
}

function variantStyles({ theme, variant = "primary" }: ButtonProps): CSSProp {
  return css`
    background: ${theme.colors.buttonBackground[variant]};
    border: ${theme.borders.button[variant]};
    color: ${theme.colors.buttonText[variant]};
    font-size: ${theme.fontSizes.button[variant]};
    font-weight: ${theme.fontWeights.button[variant]};
    letter-spacing: ${theme.letterSpacings.button[variant]};

    &:hover {
      background: ${theme.colors.buttonHoverBackground[variant]};
      border: ${theme.borders.buttonHover[variant]};
      color: ${theme.colors.buttonHoverText[variant]};
    }
  `
}

const Button = styled.button<ButtonProps>`
  cursor: pointer;
  ${beforeElementStyles};
  ${borderRadiusStyles};
  ${focusStyles};
  ${sizeVariantStyles};
  ${variantStyles};
  ${elementCss};
`

Button.defaultProps = {
  fontFamily: "text.button",
  lineHeight: 1.2,
  position: "relative",
  textDecoration: "none",
  textTransform: "uppercase",
}

export default Button
